import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { Upload, RefreshCw, Link as LinkIcon, Check } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { API_BASE_URL } from '../config/environment';


const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'align', 'link'
];

const DAY_OPTIONS = [
  { value: 0, label: 'Niedziela' },
  { value: 1, label: 'Poniedziałek' },
  { value: 2, label: 'Wtorek' },
  { value: 3, label: 'Środa' },
  { value: 4, label: 'Czwartek' },
  { value: 5, label: 'Piątek' },
  { value: 6, label: 'Sobota' }
];

const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[ąàáâãäå]/g, 'a')
    .replace(/[ćčç]/g, 'c')
    .replace(/[ęèéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[łľ]/g, 'l')
    .replace(/[ńñ]/g, 'n')
    .replace(/[óòôõö]/g, 'o')
    .replace(/[śšş]/g, 's')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[ýÿ]/g, 'y')
    .replace(/[źżž]/g, 'z')
    .replace(/[đ]/g, 'd')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    timeFrom: '',
    timeTo: '',
    description: '',
    location: '',
    maxParticipants: '',
    price: '',
    category: 'other',
    image: null,
    slug: '',
    isRecurring: false,
    recurringDays: [],
    regulations: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events`);
      const data = await response.json();
      setEvents(data.events);
    } catch (err) {
      setError('Błąd podczas pobierania wydarzeń');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      if (name === 'title' && !slugManuallyEdited) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const handleDayToggle = (dayValue) => {
    setFormData(prev => {
      const days = [...prev.recurringDays];
      const idx = days.indexOf(dayValue);
      if (idx >= 0) {
        days.splice(idx, 1);
      } else {
        days.push(dayValue);
      }
      return { ...prev, recurringDays: days };
    });
  };

  const handleSlugChange = (e) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    setFormData(prev => ({ ...prev, slug: value }));
    setSlugManuallyEdited(true);
  };

  const handleRegulationsChange = (content) => {
    setFormData(prev => ({ ...prev, regulations: content }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setError('Plik jest za duży. Maksymalny rozmiar to 5MB.');
        e.target.value = '';
        return;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setError('Niedozwolony format pliku. Dozwolone są tylko JPEG, PNG i GIF.');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result);
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const buildFormDataToSend = () => {
    const fd = new FormData();
    // Fields that must always be sent (even when empty) to allow clearing
    const alwaysSendFields = ['timeFrom', 'timeTo'];
    Object.keys(formData).forEach(key => {
      if (key === 'image' && formData[key]) {
        fd.append('image', formData[key]);
      } else if (key === 'isRecurring') {
        fd.append(key, formData[key] ? 'true' : 'false');
      } else if (key === 'recurringDays') {
        fd.append(key, JSON.stringify(formData[key]));
      } else if (alwaysSendFields.includes(key)) {
        fd.append(key, formData[key] || '');
      } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
        fd.append(key, formData[key]);
      }
    });
    // Clear legacy 'time' field — we now use timeFrom/timeTo
    fd.append('time', '');
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/events`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: buildFormDataToSend()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.errors?.join(', ') || 'Błąd podczas dodawania wydarzenia');
      await fetchEvents();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to wydarzenie?')) return;
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Błąd podczas usuwania wydarzenia');
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: buildFormDataToSend()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.errors?.join(', ') || 'Błąd podczas aktualizacji wydarzenia');
      await fetchEvents();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      timeFrom: '',
      timeTo: '',
      description: '',
      location: '',
      maxParticipants: '',
      price: '',
      category: 'other',
      image: null,
      slug: '',
      isRecurring: false,
      recurringDays: [],
      regulations: ''
    });
    setPreviewImage(null);
    setSelectedEvent(null);
    setSlugManuallyEdited(false);
  };

  const handleCopyLink = (slug) => {
    const url = `${window.location.origin}/events/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(slug);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  if (isLoading && events.length === 0) {
    return <div className="text-center">Ładowanie...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Błąd! </strong>
          <span className="block sm:inline">{error}</span>
          <button onClick={() => setError(null)} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="sr-only">Zamknij</span>
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <form onSubmit={selectedEvent ? handleUpdate : handleSubmit} className="space-y-4">
        {/* Image upload */}
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <input type="file" accept={ALLOWED_FILE_TYPES.join(',')} onChange={handleImageChange} className="hidden" id="image-upload" />
          <label htmlFor="image-upload" className="flex flex-col items-center cursor-pointer">
            {previewImage ? (
              <div className="relative w-full max-w-md">
                <img src={previewImage} alt="Preview" className="w-full h-48 object-cover rounded-lg mb-4" />
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="w-12 h-12 text-gray-400" />
                <span className="mt-2 text-sm text-gray-500">Kliknij aby dodać zdjęcie</span>
                <span className="mt-1 text-xs text-gray-400">(max. 5MB, formaty: JPEG, PNG, GIF)</span>
              </div>
            )}
          </label>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Tytuł</label>
          <input type="text" name="title" value={formData.title} onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" required />
        </div>

        {/* Slug */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Przyjazny URL (slug) <span className="text-gray-400 ml-1">— używany w linku i kodzie QR</span>
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">/events/</span>
            <input type="text" value={formData.slug} onChange={handleSlugChange} placeholder="np. pub-quiz"
              className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-red-500 focus:ring-red-500 text-sm" />
          </div>
          <p className="mt-1 text-xs text-gray-400">Generowany automatycznie z tytułu. Możesz edytować ręcznie.</p>
        </div>

        {/* Recurring event toggle */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <input type="checkbox" id="isRecurring" name="isRecurring" checked={formData.isRecurring} onChange={handleInputChange}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded" />
            <label htmlFor="isRecurring" className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
              <RefreshCw className="w-4 h-4 text-amber-600" />
              Wydarzenie cykliczne (powtarza się co tydzień)
            </label>
          </div>
          
          {formData.isRecurring && (
            <div className="mt-4 pl-7">
              <label className="block text-sm font-medium text-gray-700 mb-2">Dni tygodnia</label>
              <div className="flex flex-wrap gap-2">
                {DAY_OPTIONS.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => handleDayToggle(day.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      formData.recurringDays.includes(day.value)
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Date (optional for recurring) */}
        {!formData.isRecurring && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Data</label>
            <input type="date" name="date" value={formData.date} onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" />
          </div>
        )}

        {/* Time range (optional) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Godzina od <span className="text-gray-400">(opcjonalnie)</span>
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input type="time" name="timeFrom" value={formData.timeFrom} onChange={handleInputChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" />
              {formData.timeFrom && (
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, timeFrom: '' }))}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 
                    border border-gray-300 rounded-md hover:bg-red-50 transition-colors" title="Wyczyść">
                  ×
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Godzina do <span className="text-gray-400">(opcjonalnie)</span>
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input type="time" name="timeTo" value={formData.timeTo} onChange={handleInputChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" />
              {formData.timeTo && (
                <button type="button" onClick={() => setFormData(prev => ({ ...prev, timeTo: '' }))}
                  className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 
                    border border-gray-300 rounded-md hover:bg-red-50 transition-colors" title="Wyczyść">
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Kategoria</label>
          <select name="category" value={formData.category} onChange={handleInputChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500">
            <option value="music">Muzyka</option>
            <option value="cooking">Gotowanie</option>
            <option value="tasting">Degustacja</option>
            <option value="other">Inne</option>
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Opis</label>
          <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" required />
        </div>

        {/* Regulations (WYSIWYG) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Regulamin <span className="text-gray-400 ml-1">— możesz wkleić tekst z Worda/Google Docs</span>
          </label>
          <div className="bg-white rounded-md border border-gray-300">
            <ReactQuill
              value={formData.regulations}
              onChange={handleRegulationsChange}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Wklej lub wpisz regulamin wydarzenia..."
              theme="snow"
              style={{ minHeight: '200px' }}
            />
          </div>
        </div>

        {/* Location, Max Participants, Price (all optional) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Lokalizacja <span className="text-gray-400">(opcjonalnie)</span></label>
            <input type="text" name="location" value={formData.location} onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Max. uczestników <span className="text-gray-400">(opcjonalnie)</span></label>
            <input type="number" name="maxParticipants" value={formData.maxParticipants} onChange={handleInputChange} min="1"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Cena (zł) <span className="text-gray-400">(opcjonalnie)</span></label>
            <input type="number" step="0.01" name="price" value={formData.price} onChange={handleInputChange} min="0"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500" />
          </div>
        </div>

        {/* Submit buttons */}
        <div className="flex justify-end gap-2">
          {selectedEvent && (
            <button type="button" onClick={resetForm}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              Anuluj
            </button>
          )}
          <button type="submit" disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
            {isLoading ? 'Zapisywanie...' : selectedEvent ? 'Aktualizuj' : 'Dodaj'}
          </button>
        </div>
      </form>

      {/* Events list */}
      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Lista wydarzeń</h3>
        <div className="space-y-4">
          {events && events.length > 0 ? (
            events.map(event => {
              const isRecurring = event.isRecurring === true || event.isRecurring === 'true';
              const days = event.recurringDays || (event.recurringDay !== undefined ? [event.recurringDay] : []);
              const daysLabel = days.map(d => DAY_OPTIONS.find(o => o.value === d)?.label || '?').join(', ');
              const timeLabel = event.timeFrom
                ? (event.timeTo ? `${event.timeFrom} – ${event.timeTo}` : event.timeFrom)
                : event.time || '';

              return (
                <div key={event.id} className="bg-white shadow rounded-lg p-4 border border-gray-200">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      {event.image && (
                        <img src={`${API_BASE_URL}${event.image}`} alt={event.title}
                          className="w-full md:w-48 h-32 object-cover rounded-lg mb-4 md:mb-0 md:mr-4" />
                      )}
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-lg font-medium">{event.title}</h4>
                          {isRecurring && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <RefreshCw className="w-3 h-3" />
                              Cykliczne
                            </span>
                          )}
                          {event.regulations && event.regulations.trim() !== '' && event.regulations !== '<p><br></p>' && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                              Regulamin
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {isRecurring ? daysLabel : (event.date ? format(new Date(event.date), "d MMMM yyyy", { locale: pl }) : '—')}
                          {timeLabel && ` | ${timeLabel}`}
                        </p>
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{event.description}</p>
                        {event.location && <p className="text-sm text-gray-600 mt-1">Lokalizacja: {event.location}</p>}
                        <div className="mt-2 space-x-4 text-sm text-gray-600">
                          <span>Kategoria: {event.category}</span>
                          {event.maxParticipants && <span>Max uczestników: {event.maxParticipants}</span>}
                          {event.price && <span>Cena: {event.price} zł</span>}
                        </div>
                        {event.slug && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-gray-400">/events/{event.slug}</span>
                            <button type="button" onClick={() => handleCopyLink(event.slug)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 rounded hover:bg-blue-50 transition-colors">
                              {copiedId === event.slug ? (<><Check className="w-3 h-3" />Skopiowano!</>) : (<><LinkIcon className="w-3 h-3" />Kopiuj link</>)}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 md:mt-0 space-x-2 flex md:flex-col md:space-x-0 md:space-y-2">
                      <button
                        onClick={() => {
                          setSelectedEvent(event);
                          const rec = event.isRecurring === true || event.isRecurring === 'true';
                          setFormData({
                            title: event.title,
                            date: event.date ? format(new Date(event.date), "yyyy-MM-dd") : '',
                            timeFrom: event.timeFrom || event.time || '',
                            timeTo: event.timeTo || '',
                            description: event.description,
                            location: event.location || '',
                            maxParticipants: event.maxParticipants || '',
                            price: event.price || '',
                            category: event.category || 'other',
                            image: null,
                            slug: event.slug || '',
                            isRecurring: rec,
                            recurringDays: event.recurringDays || (event.recurringDay !== undefined ? [event.recurringDay] : []),
                            regulations: event.regulations || ''
                          });
                          setPreviewImage(event.image ? `${API_BASE_URL}${event.image}` : null);
                          setSlugManuallyEdited(!!event.slug);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-600 rounded-md hover:bg-blue-50"
                      >
                        Edytuj
                      </button>
                      <button onClick={() => handleDelete(event.id)}
                        className="px-3 py-1 text-red-600 hover:text-red-800 border border-red-600 rounded-md hover:bg-red-50">
                        Usuń
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-gray-500">Brak wydarzeń do wyświetlenia</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventsManagement;